import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { getApiUrl } from '../config';

const ResetPasswordPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Link inválido');
      return;
    }
    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(getApiUrl('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'No se pudo actualizar la contraseña');
      }
      setSuccess(true);
      setTimeout(() => navigate('/auth'), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    color: theme.colors.text,
    fontSize: theme.fonts.sizeBody,
    fontFamily: theme.fonts.body,
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: 12,
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '20px 16px' : 32,
        backgroundColor: theme.colors.background,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          padding: 28,
          border: `2px solid ${theme.colors.primary}`,
        }}
      >
        <h1
          style={{
            margin: '0 0 8px 0',
            color: theme.colors.text,
            fontSize: 22,
            fontWeight: 800,
            fontFamily: theme.fonts.heading,
            textAlign: 'center',
          }}
        >
          {success ? '¡Listo!' : 'Nueva contraseña'}
        </h1>

        {success ? (
          <>
            <p
              style={{
                margin: '12px 0 24px 0',
                color: theme.colors.textSecondary,
                fontSize: 14,
                lineHeight: 1.5,
                textAlign: 'center',
              }}
            >
              Actualizamos tu contraseña. Te redirigimos al login…
            </p>
            <button
              type="button"
              onClick={() => navigate('/auth')}
              style={{
                width: '100%',
                padding: '14px',
                background: theme.colors.primary,
                color: '#000',
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontSize: theme.fonts.sizeBody,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Ir al login
            </button>
          </>
        ) : (
          <>
            <p
              style={{
                margin: '4px 0 20px 0',
                color: theme.colors.textSecondary,
                fontSize: 14,
                lineHeight: 1.5,
                textAlign: 'center',
              }}
            >
              Elegí una contraseña nueva para tu cuenta.
            </p>
            <form onSubmit={handleSubmit}>
              <input
                type="password"
                placeholder="Nueva contraseña"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                autoFocus
                style={inputStyle}
              />
              <input
                type="password"
                placeholder="Repetí la contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                style={inputStyle}
              />
              {error && (
                <div
                  style={{
                    color: theme.colors.danger,
                    fontSize: 13,
                    marginBottom: 12,
                    textAlign: 'center',
                  }}
                >
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: theme.colors.primary,
                  color: '#000',
                  border: 'none',
                  borderRadius: theme.borderRadius.button,
                  fontSize: theme.fonts.sizeBody,
                  fontWeight: 700,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Guardando…' : 'Guardar contraseña'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
